/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttir.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalici degisiklik yapilmaz.
 * Degisiklik gerekiyorsa Core'da guncellenir ve senkron ile diger repolara yayilir.
 */

import type { DataTableCsvColumn } from './data-table-export.utils';

export interface DataTableImportIssue {
  rowNumber?: number;
  field?: string;
  header?: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface DataTableImportRow {
  rowNumber: number;
  values: Record<string, string>;
  rawValues: string[];
  errors: DataTableImportIssue[];
  warnings: DataTableImportIssue[];
}

export interface DataTableImportOptions {
  maxRows?: number;
  messages?: {
    unknownHeader?: (header: string) => string;
    missingRequiredHeader?: (header: string) => string;
    requiredCell?: (header: string) => string;
    maxRowsExceeded?: (maxRows: number) => string;
  };
}

export interface DataTableImportParseResult {
  fileName: string;
  headers: string[];
  delimiter: string;
  rows: DataTableImportRow[];
  validRows: DataTableImportRow[];
  errors: DataTableImportIssue[];
  warnings: DataTableImportIssue[];
  unknownHeaders: string[];
  missingRequiredHeaders: string[];
}

export interface DataTableImportPayload extends DataTableImportParseResult {
  file: File;
}

export interface DataTableImportSubmitResult {
  success?: boolean;
  message?: string;
  errors?: string[];
  closeModal?: boolean;
}

interface HeaderMatch {
  column: DataTableCsvColumn;
  field: string;
}

function trimBom(value: string): string {
  return value.replace(/^\uFEFF/, '');
}

function normalizeHeader(value: unknown): string {
  return trimBom(String(value ?? ''))
    .trim()
    .replace(/\s+\*$/, '')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('tr-TR');
}

function getColumnField(column: DataTableCsvColumn): string {
  return String(column.field || column.header).trim();
}

function buildHeaderLookup(columns: DataTableCsvColumn[]): Map<string, HeaderMatch> {
  const lookup = new Map<string, HeaderMatch>();
  for (const column of columns) {
    const field = getColumnField(column);
    const candidates = [column.header, ...(column.aliases ?? [])];
    for (const candidate of candidates) {
      const normalized = normalizeHeader(candidate);
      if (normalized) {
        lookup.set(normalized, { column, field });
      }
    }
  }
  return lookup;
}

function detectDelimiter(headerLine: string): string {
  const semicolonCount = (headerLine.match(/;/g) ?? []).length;
  const commaCount = (headerLine.match(/,/g) ?? []).length;
  return semicolonCount >= commaCount ? ';' : ',';
}

function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      row.push(cell);
      cell = '';
      continue;
    }

    if (!inQuotes && (char === '\n' || char === '\r')) {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      if (char === '\r' && next === '\n') {
        i += 1;
      }
      continue;
    }

    cell += char;
  }

  row.push(cell);
  rows.push(row);

  return rows.filter((cells) => cells.some((value) => String(value ?? '').trim() !== ''));
}

function buildIssue(
  severity: DataTableImportIssue['severity'],
  message: string,
  options: Pick<DataTableImportIssue, 'rowNumber' | 'field' | 'header'> = {},
): DataTableImportIssue {
  return { severity, message, ...options };
}

export async function parseDataTableImportFile(
  file: File,
  columns: DataTableCsvColumn[],
  options: DataTableImportOptions = {},
): Promise<DataTableImportPayload> {
  const text = await file.text();
  return {
    file,
    ...parseDataTableImportText(file.name, text, columns, options),
  };
}

export function parseDataTableImportText(
  fileName: string,
  text: string,
  columns: DataTableCsvColumn[],
  options: DataTableImportOptions = {},
): DataTableImportParseResult {
  const normalizedText = trimBom(String(text ?? ''));
  const messages = options.messages ?? {};
  const firstLine = normalizedText.split(/\r?\n/, 1)[0] ?? '';
  const delimiter = detectDelimiter(firstLine);
  const parsedRows = parseDelimited(normalizedText, delimiter);
  const rawHeaders = parsedRows[0] ?? [];
  const headers = rawHeaders.map((header) => trimBom(header).trim());
  const headerLookup = buildHeaderLookup(columns);
  const matchedHeaders = new Map<number, HeaderMatch>();
  const seenRequired = new Set<string>();
  const unknownHeaders: string[] = [];
  const errors: DataTableImportIssue[] = [];
  const warnings: DataTableImportIssue[] = [];

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    const match = headerLookup.get(normalized);
    if (match) {
      matchedHeaders.set(index, match);
      if (match.column.required) {
        seenRequired.add(getColumnField(match.column));
      }
    } else if (header.trim()) {
      unknownHeaders.push(header);
      warnings.push(buildIssue('warning', messages.unknownHeader?.(header) ?? `Unknown column: ${header}`, { header }));
    }
  });

  const missingRequiredHeaders = columns
    .filter((column) => column.required && !seenRequired.has(getColumnField(column)))
    .map((column) => column.header);

  for (const header of missingRequiredHeaders) {
    errors.push(buildIssue('error', messages.missingRequiredHeader?.(header) ?? `Missing required column: ${header}`, { header }));
  }

  const maxRows = options.maxRows && options.maxRows > 0 ? options.maxRows : undefined;
  const bodyRows = parsedRows.slice(1, maxRows ? maxRows + 1 : undefined);
  if (maxRows && parsedRows.length - 1 > maxRows) {
    warnings.push(buildIssue('warning', messages.maxRowsExceeded?.(maxRows) ?? `The file has more than ${maxRows} rows; only the first ${maxRows} rows were evaluated.`));
  }

  const rows = bodyRows.map((rawValues, rowIndex) => {
    const rowNumber = rowIndex + 2;
    const values: Record<string, string> = {};
    const rowErrors: DataTableImportIssue[] = [];
    const rowWarnings: DataTableImportIssue[] = [];

    matchedHeaders.forEach((match, columnIndex) => {
      values[match.field] = String(rawValues[columnIndex] ?? '').trim();
    });

    for (const column of columns) {
      const field = getColumnField(column);
      if (column.required && !String(values[field] ?? '').trim()) {
        rowErrors.push(
          buildIssue('error', messages.requiredCell?.(column.header) ?? `${column.header} is required.`, {
            rowNumber,
            field,
            header: column.header,
          }),
        );
      }
    }

    return {
      rowNumber,
      values,
      rawValues,
      errors: rowErrors,
      warnings: rowWarnings,
    };
  });

  const rowErrors = rows.flatMap((row) => row.errors);
  const rowWarnings = rows.flatMap((row) => row.warnings);
  const allErrors = [...errors, ...rowErrors];
  const allWarnings = [...warnings, ...rowWarnings];

  return {
    fileName,
    headers,
    delimiter,
    rows,
    validRows: rows.filter((row) => row.errors.length === 0),
    errors: allErrors,
    warnings: allWarnings,
    unknownHeaders,
    missingRequiredHeaders,
  };
}
